import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/loading/LoadingSprinner';
import { becomeAProvider } from '../../../service/userService';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { handleProviderLogin } from '../../components/auth/apiAuth';

const MemebershipPlanCallBack = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const SUCCESS_FLAG_KEY = 'provider_trial_success';
  const { isAuthenticated, userId, role } = useAuth();
  const [message, setMessage] = useState(t('membership_callback.processing'));

  // const handleBecomeProvider = async (membershipPlanName: string) => {
  //   try {
  //   //   setIsLoading(true);
  //     if (!userId) {
  //       throw new Error('User ID is missing');
  //     }
  //     if (!isAuthenticated) {
  //       throw new Error('User is not authenticated');
  //     }

  //     const res = await becomaAProvider(userId, membershipPlanName);

  //     // await new Promise((resolve) => setTimeout(resolve, 2000));
  //     // const res = { success: true };
  //     console.log('Become a provider response: ', res);

  //     if (res.success) {
  //       sessionStorage.setItem(SUCCESS_FLAG_KEY, 'true');
  //       localStorage.removeItem('membershipPlanState');
  //       localStorage.removeItem('membershipPlanName');
  //       setTimeout(() => window.location.href = '/home', 400);
  //     }
  //   } catch (err: any) {
  //     console.error('Become a provider failed', err);
  //     localStorage.removeItem('membershipPlanState');
  //     localStorage.removeItem('membershipPlanName');
  //     addToast({
  //       title: 'Error becoming a provider',
  //       description: err
  //         ? err.message
  //         : 'Something went wrong. Please try again later.',
  //       radius: 'md',
  //       color: 'danger',
  //     });
  //   }
  // };

  // useEffect(() => {
  //   const handleMembershipPlanCallback = async () => {
  //     console.log('Running membership plan callback');
  //     const membershipPlanState = localStorage.getItem('membershipPlanState');
  //     const membershipPlanName = localStorage.getItem('membershipPlanName');
  //     if (membershipPlanState !== 'true') {
  //       console.warn(
  //         'Direct access to /membership-plan-callback detected. Redirecting.'
  //       );
  //       navigate('/home', { replace: true });

  //       return;
  //     }
  //     console.log('Continue membership plan callback');
  //     try {
  //       setMessage('Finalizing membership plan...');
  //       await handleBecomeProvider(membershipPlanName || 'free');
  //     } catch (error) {
  //       console.error('Error during membership plan finalization:', error);
  //       setMessage('An error occurred during membership plan.');
  //       localStorage.removeItem('membershipPlanState');
  //       localStorage.removeItem('membershipPlanName');
  //       navigate('/home', { replace: true });
  //     }
  //   };

  //   handleMembershipPlanCallback();
  // }, []);

  const handleBecomeProvider = async (
    membershipPlanName: string,
    businessId?: string
  ) => {
    try {
      const res = await becomeAProvider(
        userId!,
        membershipPlanName,
        businessId || undefined
      );

      if (res.success) {
        sessionStorage.setItem(SUCCESS_FLAG_KEY, 'true');
        localStorage.removeItem('membershipPlanState');
        localStorage.removeItem('membershipPlanName');

        // The DB role and Cognito group are now `provider`, but the live session
        // still holds the `customer` role snapshot taken at login. A bare
        // redirect to /home keeps that stale session — so the provider portal
        // 403s and the user lands on the customer site instead. Re-login against
        // the provider portal: Cognito SSO rebuilds the session as `provider` and
        // the backend callback lands the user directly on the provider dashboard.
        handleProviderLogin();
      }
    } catch (err: any) {
      console.error('Become a provider failed', err);

      localStorage.removeItem('membershipPlanState');
      localStorage.removeItem('membershipPlanName');

      addToast({
        title: t('membership_callback.error_becoming_provider'),
        description:
          err?.message || t('membership_callback.something_went_wrong'),
        radius: 'md',
        color: 'danger',
      });
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !userId) return; // wait until auth is ready

    const handleMembershipPlanCallback = async () => {
      // Check for "Add a new business" flow — Stripe redirects with ?businessId=
      const params = new URLSearchParams(window.location.search);
      const newBusinessId = params.get('businessId');
      const membershipPlanName =
        localStorage.getItem('membershipPlanName') || 'free';
      if (newBusinessId) {
        // Check whether this is a customer→provider upgrade or an existing
        // provider adding a new business.
        const isAlreadyProvider = role === 'provider';

        if (isAlreadyProvider) {
          // Existing provider — create the business directly.
          try {
            setMessage('Creating your new business...');
            const res = await fetch('/user/api/v1/business', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'x-active-role': 'provider',
                'x-client-portal': 'provider',
              },
              body: JSON.stringify({
                name: 'My Business',
                businessId: newBusinessId,
                membershipPlan: membershipPlanName,
              }),
            });
            if (res.ok) {
              window.location.href = `${import.meta.env.VITE_PROVIDER_DOMAIN}/dashboard`;
            } else {
              const err = await res.json().catch(() => ({}));
              throw new Error(err?.message || 'Failed to create business');
            }
          } catch (error: any) {
            console.error('Failed to create business after payment:', error);
            addToast({
              title: 'Business creation failed',
              description:
                'Your payment succeeded but we could not create the business. Please contact support.',
              radius: 'md',
              color: 'danger',
            });
            window.location.href = `${import.meta.env.VITE_PROVIDER_DOMAIN}/dashboard`;
          }
          return;
        }

        // Customer→provider upgrade — call becomeAProvider with the
        // pre-reserved businessId so the subscription gets BusinessId.
        try {
          setMessage(t('membership_callback.finalizing'));
          const membershipPlanName =
            localStorage.getItem('membershipPlanName') || 'free';
          await handleBecomeProvider(membershipPlanName, newBusinessId);
        } catch (error) {
          console.error('Become a provider failed', error);
          localStorage.removeItem('membershipPlanState');
          localStorage.removeItem('membershipPlanName');
          window.location.href = '/home';
        }
        return;
      }

      // Existing flow: customer → provider upgrade
      const membershipPlanState = localStorage.getItem('membershipPlanState');

      if (membershipPlanState !== 'true') {
        console.warn(
          'Direct access to /membership-plan-callback detected. Redirecting.'
        );
        navigate('/home', { replace: true });
        return;
      }

      console.log('Continue membership plan callback');

      try {
        setMessage(t('membership_callback.finalizing'));
        await handleBecomeProvider(membershipPlanName || 'free');
      } catch (error) {
        console.error('Error during membership plan finalization:', error);
        setMessage(t('membership_callback.error'));

        localStorage.removeItem('membershipPlanState');
        localStorage.removeItem('membershipPlanName');

        navigate('/home', { replace: true });
      }
    };

    handleMembershipPlanCallback();
  }, [userId, isAuthenticated]);

  return <LoadingSpinner label={message} />;
};

export default MemebershipPlanCallBack;
